import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { TrendingUp, TrendingDown, Activity, X as XIcon, Zap, LineChart as LineChartIcon, Bell, BellOff, Move } from 'lucide-react';
import { ALL_SYMBOLS, ASSET_CLASSES, TIMEFRAMES, subscribeTicker, subscribeMultiTicker, fetch24hr, fmtPrice } from '../../lib/market';
import TerminalChart from '../../components/TerminalChart';
import TradingViewChart from '../../components/TradingViewChart';
import { setVolatilityFactor } from '../../lib/volatility';
import { useToast } from '../../hooks/use-toast';
import { positionsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  notificationsAllowed, notificationsBlocked, requestNotificationsPermission,
  notifyStopLossHit, notifyTakeProfitHit, notifyTrailingStopMoved, clearFiredKey
} from '../../lib/notifications';


function parseErr(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => (typeof e === 'string' ? e : (e.msg || 'invalid'))).join('; ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || 'Try again';
}

// Per-row inline trailing-stop control. Toggle ON to auto-ratchet the SL as
// price moves in the profitable direction. Quick presets: 0.5% / 1% / 2%.
function TrailControl({ pos, onToggle }) {
  const active = (pos.trailing_pct > 0) || (pos.trailing_distance > 0);
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState(pos.trailing_mode || 'pct');
  const [custom, setCustom] = React.useState(pos.trailing_pct || '');
  const [dist, setDist] = React.useState(pos.trailing_distance || '');
  const [activation, setActivation] = React.useState(pos.trailing_activation || '');

  const applyPct = (pct) => { onToggle(pos, { mode: 'pct', pct, activation: parseFloat(activation) || 0 }); setOpen(false); };
  const applyPrice = () => {
    const d = parseFloat(dist);
    if (d > 0) { onToggle(pos, { mode: 'price', distance: d, activation: parseFloat(activation) || 0 }); setOpen(false); }
  };
  const turnOff = () => { onToggle(pos, { mode: 'pct', pct: 0, activation: 0 }); setOpen(false); };

  const label = active
    ? (pos.trailing_mode === 'price' ? `${pos.trailing_distance}` : `${(pos.trailing_pct || 0).toFixed(2)}%`)
    : 'Off';

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(v => !v)}
        data-testid={`trail-toggle-${pos.id}`}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold transition ${
          active
            ? 'border-[#00e676]/50 text-[#00e676] bg-[#00e676]/10'
            : 'border-[#1a2823] text-gray-500 hover:text-[#00e676] hover:border-[#00e676]/40'
        }`}
        title={active ? 'Trailing active' : 'Enable trailing stop'}
      >
        <Move size={10}/> {label}
      </button>
      {open && (
        <div
          className="absolute z-40 top-full left-0 mt-1 w-48 p-2 rounded-lg border border-[#00e676]/30 bg-[#050807] shadow-xl"
          onClick={e => e.stopPropagation()}
          data-testid={`trail-panel-${pos.id}`}
        >
          <div className="flex gap-1 mb-2">
            <button onClick={() => setMode('pct')} data-testid={`trail-mode-pct-${pos.id}`}
                    className={`flex-1 py-0.5 text-[10px] rounded border ${mode === 'pct' ? 'border-[#00e676] text-[#00e676] bg-[#00e676]/10' : 'border-[#1a2823] text-gray-400'}`}>Percent %</button>
            <button onClick={() => setMode('price')} data-testid={`trail-mode-price-${pos.id}`}
                    className={`flex-1 py-0.5 text-[10px] rounded border ${mode === 'price' ? 'border-[#00e676] text-[#00e676] bg-[#00e676]/10' : 'border-[#1a2823] text-gray-400'}`}>Price $</button>
          </div>

          {mode === 'pct' ? (
            <>
              <div className="text-[10px] text-gray-400 mb-1 uppercase tracking-wider">Trailing stop %</div>
              <div className="flex gap-1 mb-1.5">
                {[0.5, 1, 2, 5].map(v => (
                  <button key={v} onClick={() => applyPct(v)}
                          data-testid={`trail-preset-${pos.id}-${v}`}
                          className="flex-1 py-0.5 text-[10px] rounded border border-[#1a2823] text-gray-300 hover:border-[#00e676] hover:text-[#00e676]">{v}%</button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <input type="number" step="0.1" min="0" value={custom} onChange={e => setCustom(e.target.value)} placeholder="Custom %"
                       className="flex-1 bg-[#0a1210] border border-[#1a2823] rounded px-2 py-1 text-[11px] text-white mono focus:outline-none focus:border-[#00e676]"/>
                <button onClick={() => { const v = parseFloat(custom); if (v > 0 && v < 50) applyPct(v); }} className="btn-green px-2 py-1 rounded text-[10px] font-bold">Set</button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] text-gray-400 mb-1 uppercase tracking-wider">Trail distance (price)</div>
              <div className="flex items-center gap-1">
                <input type="number" step="any" min="0" value={dist} onChange={e => setDist(e.target.value)} placeholder="e.g. 5.00"
                       data-testid={`trail-distance-${pos.id}`}
                       className="flex-1 bg-[#0a1210] border border-[#1a2823] rounded px-2 py-1 text-[11px] text-white mono focus:outline-none focus:border-[#00e676]"/>
                <button onClick={applyPrice} data-testid={`trail-distance-set-${pos.id}`} className="btn-green px-2 py-1 rounded text-[10px] font-bold">Set</button>
              </div>
            </>
          )}

          <div className="mt-2 text-[10px] text-gray-400 mb-1 uppercase tracking-wider">Activate after profit {mode === 'price' ? '(price)' : '%'}</div>
          <input type="number" step="any" min="0" value={activation} onChange={e => setActivation(e.target.value)} placeholder="0 = immediate"
                 data-testid={`trail-activation-${pos.id}`}
                 className="w-full bg-[#0a1210] border border-[#1a2823] rounded px-2 py-1 text-[11px] text-white mono focus:outline-none focus:border-[#00e676]"/>
          {active && (
            <button onClick={turnOff} data-testid={`trail-off-${pos.id}`}
                    className="mt-1.5 w-full py-0.5 text-[10px] rounded border border-red-500/40 text-red-400 hover:bg-red-500/10">Turn OFF</button>
          )}
        </div>
      )}
    </div>
  );
}

const Terminal = () => {
  const [selected, setSelected] = useState(ALL_SYMBOLS[0]);
  const [interval, setInterval] = useState('1m');
  const [market, setMarket] = useState('spot');
  const [assetClass, setAssetClass] = useState('all');
  const [indicators, setIndicators] = useState({ ema: false, sma: false, bb: false, ichimoku: false, rsi: false, macd: false });
  const [ticker, setTicker] = useState({ last: 0, changePct: 0, high: 0, low: 0, quoteVolume: 0, bid: 0, ask: 0 });
  const [prices, setPrices] = useState({});
  const [positions, setPositions] = useState([]);
  const [loadingPos, setLoadingPos] = useState(false);
  const [orderMode, setOrderMode] = useState('Market');
  const [volume, setVolume] = useState('0.010');
  const [leverage, setLeverage] = useState(100);
  const [limitPrice, setLimitPrice] = useState('');
  const [sl, setSl] = useState('');
  const [tp, setTp] = useState('');
  const [placingOrder, setPlacingOrder] = useState(null); // null | 'Buy' | 'Sell'
  const [notifState, setNotifState] = useState(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const lastTrailPatch = useRef({});   // { positionId: timestamp } — debounce
  const stopOutRef = useRef(0);        // debounce stop-out checks
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const hasFunds = (user?.balance ?? 0) > 0;
  useEffect(() => { setVolatilityFactor(user?.volatility_factor || 1); }, [user]);

  // Load positions from backend
  const loadPositions = useCallback(async () => {
    try {
      const list = await positionsAPI.list();
      setPositions(list);
    } catch (e) { /* handled by interceptor */ }
  }, []);

  useEffect(() => { loadPositions(); }, [loadPositions]);

  // Selected symbol ticker
  useEffect(() => {
    // Reset ticker to CLEAN state on every symbol/provider/market switch so
    // the previous symbol's price / 24H high / low / volume don't blink
    // through before the new symbol's fresh snapshot arrives. Use undefined
    // (not 0) so the display guards render "-" instead of stale zeros.
    setTicker({
      last: undefined, changePct: undefined,
      high: undefined, low: undefined,
      quoteVolume: undefined, bid: undefined, ask: undefined
    });
    let ws;
    let cancelled = false;
    (async () => {
      try {
        const snap = await fetch24hr(selected.symbol, market, selected.provider);
        if (cancelled) return;
        setTicker(prev => ({
          ...prev,
          last: parseFloat(snap.lastPrice),
          changePct: parseFloat(snap.priceChangePercent),
          high: parseFloat(snap.highPrice),
          low: parseFloat(snap.lowPrice),
          quoteVolume: parseFloat(snap.quoteVolume || 0),
          bid: parseFloat(snap.bidPrice || snap.lastPrice),
          ask: parseFloat(snap.askPrice || snap.lastPrice)
        }));
      } catch (e) {}
      if (cancelled) return;
      ws = subscribeTicker(selected.symbol, (t) => {
        // MERGE updates rather than replace so partial emits (e.g. Finnhub
        // returning null high/low outside market hours) don't wipe out
        // previously-valid values — this eliminates the 24H Low/Volume flicker.
        setTicker(prev => {
          const next = { ...prev };
          for (const [k, v] of Object.entries(t)) {
            if (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) continue;
            next[k] = v;
          }
          return next;
        });
      }, market, selected.provider);
    })();
    return () => { cancelled = true; if (ws) { try { ws.close(); } catch {} } };
  }, [selected.symbol, market, selected.provider]);

  // Multi-symbol watchlist stream — provider-aware
  useEffect(() => {
    setPrices({});
    const filtered = assetClass === 'all' ? ALL_SYMBOLS : ALL_SYMBOLS.filter(s => s.class === assetClass);
    const ws = subscribeMultiTicker(filtered, (t) => {
      setPrices(prev => {
        const merged = { ...(prev[t.symbol] || {}) };
        for (const [k, v] of Object.entries(t)) {
          if (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) continue;
          merged[k] = v;
        }
        return { ...prev, [t.symbol]: merged };
      });
    }, market);
    return () => { try { ws.close(); } catch {} };
  }, [market, assetClass]);

  // Live ticker for any symbol the user has an open position in but which is
  // NOT covered by the current watchlist filter — keeps P&L updating live.
  useEffect(() => {
    if (!positions || positions.length === 0) return;
    const filteredSyms = new Set(
      (assetClass === 'all' ? ALL_SYMBOLS : ALL_SYMBOLS.filter(s => s.class === assetClass))
        .map(s => s.symbol)
    );
    const need = positions
      .filter(p => !filteredSyms.has(p.symbol) && p.symbol !== selected.symbol)
      .map(p => ALL_SYMBOLS.find(s => s.symbol === p.symbol))
      .filter(Boolean);
    if (need.length === 0) return;
    const ws = subscribeMultiTicker(need, (t) => {
      setPrices(prev => {
        const merged = { ...(prev[t.symbol] || {}) };
        for (const [k, v] of Object.entries(t)) {
          if (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) continue;
          merged[k] = v;
        }
        return { ...prev, [t.symbol]: merged };
      });
    }, market);
    return () => { try { ws.close(); } catch {} };
  }, [positions, assetClass, selected.symbol, market]);

  // Compute positions with live P&L using ticker prices
  const positionsWithPnL = useMemo(() => {
    return positions.map(p => {
      const priceInfo = prices[p.symbol];
      const curr = (priceInfo?.last) ?? (p.symbol === selected.symbol ? ticker.last : p.open_price);
      const diff = p.side === 'Buy' ? (curr - p.open_price) : (p.open_price - curr);
      const lev = p.leverage || user?.leverage || 1;
      const pnl = diff * p.volume * lev;
      const pnlPct = (diff / p.open_price) * 100;
      return { ...p, currentPrice: curr, pnl, pnlPct };
    });
  }, [positions, prices, ticker.last, selected.symbol]);

  const totalPnL = positionsWithPnL.reduce((a, p) => a + p.pnl, 0);

  // ---- Margin / equity / stop-out ----
  const STOP_OUT_LEVEL = 50; // % — auto-close positions when margin level drops below this
  const posMargin = (p) => {
    const mult = p.symbol === 'XAUUSD' ? 100 : 1;
    const lev = p.leverage || user?.leverage || 1;
    return (p.volume * p.open_price * mult) / lev;
  };
  const usedMargin = positionsWithPnL.reduce((a, p) => a + posMargin(p), 0);
  const equity = (user?.balance ?? 0) + totalPnL;
  const marginLevel = usedMargin > 0 ? (equity / usedMargin) * 100 : null;

  // Required margin for XAU/USD: (lot × contractSize × price) / leverage.
  // Gold standard contract size is 100 oz. Leverage is the trader-selected leverage.
  const GOLD_CONTRACT_SIZE = 100;
  const levLabel = leverage >= 5000 ? '1:Unlimited' : `1:${leverage}`;
  const requiredMargin = selected.symbol === 'XAUUSD'
    ? ((parseFloat(volume) || 0) * GOLD_CONTRACT_SIZE * (ticker.ask || ticker.last || 0)) / leverage
    : null;

  // Set trailing stop. settings: { mode, pct?, distance?, activation }. pct=0/distance=0 disables.
  const togglePositionTrailing = useCallback(async (pos, settings) => {
    const { mode = 'pct', pct = 0, distance = 0, activation = 0 } = settings || {};
    const enabling = mode === 'price' ? distance > 0 : pct > 0;
    try {
      const updated = await positionsAPI.update(pos.id, {
        trailing_mode: mode,
        trailing_pct: mode === 'pct' ? pct : 0,
        trailing_distance: mode === 'price' ? distance : 0,
        trailing_activation: enabling ? activation : 0
      });
      setPositions(prev => prev.map(p => p.id === pos.id ? { ...p, ...updated } : p));
      lastTrailPatch.current[pos.id] = 0;
      if (enabling) {
        const trailTxt = mode === 'price' ? `${distance} behind price` : `${pct}% behind price`;
        const actTxt = activation > 0 ? ` once ${activation}${mode === 'price' ? '' : '%'} in profit` : '';
        toast({ title: `Trailing ON · ${pos.display || pos.symbol}`, description: `SL will ratchet ${trailTxt}${actTxt}.` });
      } else {
        toast({ title: `Trailing OFF · ${pos.display || pos.symbol}` });
      }
    } catch (err) {
      toast({ title: 'Failed to set trailing', description: parseErr(err) });
    }
  }, [toast]);

  // -------- Trailing stop engine + SL/TP hit notifications --------
  // Runs whenever prices/positions update. For each open position with a
  // trailing distance, ratchets the SL toward the current price (only in the
  // profitable direction). PATCHes the backend at most once every 2s per
  // position. Also fires browser notifications when SL/TP is crossed.
  useEffect(() => {
    if (!positionsWithPnL.length) return;

    // ---- Margin call / stop-out: when margin level <= STOP_OUT_LEVEL, auto-close
    // the worst-losing position (repeats each tick until the level recovers).
    if (usedMargin > 0 && (equity / usedMargin) * 100 <= STOP_OUT_LEVEL) {
      const now = Date.now();
      if (now - stopOutRef.current > 3000) {
        stopOutRef.current = now;
        const worst = positionsWithPnL.filter(x => !x._closing).sort((a, b) => a.pnl - b.pnl)[0];
        if (worst && Number.isFinite(worst.currentPrice) && worst.currentPrice > 0) {
          (async () => {
            try {
              await positionsAPI.close(worst.id, worst.currentPrice);
              setPositions(prev => prev.filter(x => x.id !== worst.id));
              refresh();
              toast({ title: `Stop-out · ${worst.display || worst.symbol}`, description: `Margin level below ${STOP_OUT_LEVEL}% — position auto-closed.` });
            } catch {}
          })();
        }
      }
    }

    positionsWithPnL.forEach(p => {
      const curr = p.currentPrice;
      if (!Number.isFinite(curr) || curr <= 0) return;

      // ---- SL/TP hit notification (and auto-close) ----
      if (p.sl > 0) {
        const hit = p.side === 'Buy' ? curr <= p.sl : curr >= p.sl;
        if (hit) {
          notifyStopLossHit(p, curr);
          // Auto-close the position at the SL price
          if (!p._closing) {
            (async () => {
              try {
                await positionsAPI.close(p.id, p.sl);
                setPositions(prev => prev.filter(x => x.id !== p.id));
                refresh();
                toast({ title: `SL hit · ${p.display || p.symbol}`, description: `Closed at ${p.sl}` });
              } catch {}
            })();
          }
          return;
        }
      }
      if (p.tp > 0) {
        const hit = p.side === 'Buy' ? curr >= p.tp : curr <= p.tp;
        if (hit) {
          notifyTakeProfitHit(p, curr);
          if (!p._closing) {
            (async () => {
              try {
                await positionsAPI.close(p.id, p.tp);
                setPositions(prev => prev.filter(x => x.id !== p.id));
                refresh();
                toast({ title: `TP hit · ${p.display || p.symbol}`, description: `Closed at ${p.tp}` });
              } catch {}
            })();
          }
          return;
        }
      }

      // ---- Trailing stop ratchet (mode: 'pct' = % of entry, 'price' = absolute) ----
      const mode = p.trailing_mode || 'pct';
      const dist = Number(p.trailing_distance) || 0;
      const pct = Number(p.trailing_pct) || 0;
      const effDist = mode === 'price' ? dist : (pct > 0 ? curr * (pct / 100) : 0);
      if (effDist <= 0) return;

      // ---- Activation threshold: don't start trailing until the trade is in
      // profit by trailing_activation (% of entry, or absolute price in price mode).
      const act = Number(p.trailing_activation) || 0;
      if (act > 0) {
        const profitMove = p.side === 'Buy' ? (curr - p.open_price) : (p.open_price - curr);
        const threshold = mode === 'price' ? act : p.open_price * (act / 100);
        if (profitMove < threshold) return;
      }

      let candidate;
      if (p.side === 'Buy') {
        candidate = curr - effDist;
        // Only ratchet UP (protect profits, never widen risk)
        if (!(candidate > (p.sl || 0))) return;
      } else {
        candidate = curr + effDist;
        // For Sell, only ratchet DOWN
        if (p.sl > 0 && !(candidate < p.sl)) return;
        if (!p.sl && !Number.isFinite(candidate)) return;
      }
      // Debounce: at most once every 2s per position
      const now = Date.now();
      const lastAt = lastTrailPatch.current[p.id] || 0;
      if (now - lastAt < 2000) return;
      lastTrailPatch.current[p.id] = now;

      // Round to symbol precision to keep the PATCH clean
      const rounded = Number(candidate.toFixed(p.precision || 2));
      const oldSL = p.sl || 0;

      (async () => {
        try {
          const updated = await positionsAPI.update(p.id, { sl: rounded });
          setPositions(prev => prev.map(x => x.id === p.id ? { ...x, ...updated } : x));
          notifyTrailingStopMoved(p, oldSL, rounded);
        } catch {
          // If backend rejects (e.g. still on wrong side of entry), skip silently
        }
      })();
    });
    // Only run when positionsWithPnL identity changes (price/positions update)
  }, [positionsWithPnL]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear notification dedupe keys when positions close
  useEffect(() => {
    const openIds = new Set(positions.map(p => p.id));
    // No cleanup needed for closed positions since keys include id; they simply won't refire
    void openIds;
  }, [positions]);

  const placeOrder = async (side) => {
    if (placingOrder) return; // prevent double-click
    if (!hasFunds) { toast({ title: 'Deposit required', description: 'Add funds to your account before you can trade.' }); return; }
    const vol = parseFloat(volume) || 0;
    if (vol <= 0) { toast({ title: 'Invalid volume', description: 'Enter a positive volume.' }); return; }
    // XAU/USD: enforce required margin = (lot × 100 × ask) / leverage against balance.
    if (selected.symbol === 'XAUUSD') {
      const ask = ticker.ask || ticker.last || 0;
      const margin = (vol * GOLD_CONTRACT_SIZE * ask) / leverage;
      const bal = user?.balance ?? 0;
      if (margin > bal) {
        toast({ title: 'Insufficient margin', description: `XAU/USD needs $${margin.toFixed(2)} margin (${levLabel}). Your balance is $${bal.toFixed(2)}.` });
        return;
      }
    }
    let entry;
    if (orderMode === 'Market') {
      entry = ticker.last;
    } else {
      const lp = parseFloat(limitPrice);
      if (!lp) { toast({ title: 'Enter price', description: 'Limit / Stop orders require a price.' }); return; }
      entry = lp;
    }
    if (!entry) { toast({ title: 'No price yet', description: 'Waiting for market data.' }); return; }

    // Account spread is applied server-side using this pip size (admin-configurable).
    const pipSize = selected.pip || Math.pow(10, -(selected.pricePrecision || 2));

    // Validate SL/TP against side + entry to catch obvious mistakes
    const slNum = parseFloat(sl) || 0;
    const tpNum = parseFloat(tp) || 0;
    if (slNum > 0) {
      if (side === 'Buy' && slNum >= entry) { toast({ title: 'Invalid SL', description: 'Stop Loss must be BELOW entry for a Buy.' }); return; }
      if (side === 'Sell' && slNum <= entry) { toast({ title: 'Invalid SL', description: 'Stop Loss must be ABOVE entry for a Sell.' }); return; }
    }
    if (tpNum > 0) {
      if (side === 'Buy' && tpNum <= entry) { toast({ title: 'Invalid TP', description: 'Take Profit must be ABOVE entry for a Buy.' }); return; }
      if (side === 'Sell' && tpNum >= entry) { toast({ title: 'Invalid TP', description: 'Take Profit must be BELOW entry for a Sell.' }); return; }
    }

    setPlacingOrder(side);
    try {
      const payload = {
        symbol: selected.symbol,
        display: selected.display,
        side,
        volume: vol,
        open_price: entry,
        pip: pipSize,
        leverage: leverage,
        sl: slNum,
        tp: tpNum,
        mode: orderMode,
        precision: selected.pricePrecision,
        market: selected.provider === 'synth' ? selected.class : market
      };
      const created = await positionsAPI.open(payload);
      setPositions(prev => [created, ...prev]);
      toast({ title: `${side} ${orderMode} filled`, description: `${vol} ${selected.display} @ ${fmtPrice(created.open_price || entry, selected.pricePrecision)}` });
      // Reset order-form fields so the phantom SL/TP lines clear and the user
      // doesn't accidentally re-submit stale values on the next click.
      setSl(''); setTp(''); setLimitPrice('');
    } catch (err) {
      toast({ title: 'Order failed', description: parseErr(err) });
    } finally {
      setPlacingOrder(null);
    }
  };

  const closePosition = async (id) => {
    const pos = positionsWithPnL.find(p => p.id === id);
    if (!pos) return;
    // Prevent double-click by tagging the row as closing
    if (pos._closing) return;
    setPositions(prev => prev.map(p => p.id === id ? { ...p, _closing: true } : p));
    // Fetch a FRESH live price at the moment of close so we don't use a stale
    // watchlist tick (which can be $0 for positions in other asset classes).
    let closePrice = pos.currentPrice || pos.open_price;
    try {
      const snap = await fetch24hr(pos.symbol, pos.market || market,
        selected.symbol === pos.symbol ? selected.provider :
          (ALL_SYMBOLS.find(s => s.symbol === pos.symbol)?.provider || 'binance'));
      const px = parseFloat(snap?.lastPrice);
      if (Number.isFinite(px) && px > 0) closePrice = px;
    } catch {}
    try {
      const res = await positionsAPI.close(id, closePrice);
      setPositions(prev => prev.filter(p => p.id !== id));
      toast({ title: 'Position closed', description: `P&L ${res.trade.pnl >= 0 ? '+' : ''}$${res.trade.pnl.toFixed(2)}` });
      refresh();
    } catch (err) {
      // Un-mark closing on failure so user can retry
      setPositions(prev => prev.map(p => p.id === id ? { ...p, _closing: false } : p));
      toast({ title: 'Close failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const closeAll = async () => {
    if (positions.length === 0 || loadingPos) return;
    setLoadingPos(true);
    // Snapshot the list so we don't loop over a mutating array
    const snapshot = [...positionsWithPnL];
    for (const p of snapshot) {
      // Fetch fresh price per symbol
      let px = p.currentPrice || p.open_price;
      try {
        const provider = ALL_SYMBOLS.find(s => s.symbol === p.symbol)?.provider || 'binance';
        const snap = await fetch24hr(p.symbol, p.market || market, provider);
        const fp = parseFloat(snap?.lastPrice);
        if (Number.isFinite(fp) && fp > 0) px = fp;
      } catch {}
      try { await positionsAPI.close(p.id, px); } catch {}
    }
    await loadPositions();
    refresh();
    setLoadingPos(false);
    toast({ title: 'All positions closed' });
  };

  const toggleIndicator = (key) => setIndicators(prev => ({ ...prev, [key]: !prev[key] }));

  // SL/TP + open position price lines for the current chart symbol
  const chartPriceLines = React.useMemo(() => {
    const lines = [];
    const slNum = parseFloat(sl);
    const tpNum = parseFloat(tp);
    if (Number.isFinite(slNum) && slNum > 0) {
      lines.push({ price: slNum, color: '#f97316', style: 'dashed', title: `SL (preview)` });
    }
    if (Number.isFinite(tpNum) && tpNum > 0) {
      lines.push({ price: tpNum, color: '#00e676', style: 'dashed', title: `TP (preview)` });
    }
    positionsWithPnL
      .filter(p => p.symbol === selected.symbol)
      .forEach((p, i) => {
        lines.push({
          price: p.open_price,
          color: p.side === 'Buy' ? '#22c55e' : '#ef4444',
          style: 'solid',
          title: `${p.side.toUpperCase()} #${(p.id || '').slice(0, 4)} · ${p.volume}`
        });
        if (p.sl && p.sl > 0) {
          lines.push({ price: p.sl, color: '#f97316', style: 'dashed', title: `SL #${(p.id || '').slice(0, 4)}` });
        }
        if (p.tp && p.tp > 0) {
          lines.push({ price: p.tp, color: '#00e676', style: 'dashed', title: `TP #${(p.id || '').slice(0, 4)}` });
        }
      });
    return lines;
  }, [sl, tp, positionsWithPnL, selected.symbol]);

  // Quick % helpers for SL / TP based on current price
  const applySlPct = (pct) => {
    const px = ticker.last || ticker.ask || ticker.bid;
    if (!px) return;
    setSl((px * (1 - pct / 100)).toFixed(selected.pricePrecision || 2));
  };
  const applyTpPct = (pct) => {
    const px = ticker.last || ticker.ask || ticker.bid;
    if (!px) return;
    setTp((px * (1 + pct / 100)).toFixed(selected.pricePrecision || 2));
  };

  return (
    <div className="space-y-4">
      {/* Symbol header */}
      <div className="p-3 sm:p-4 rounded-xl border border-[#1a2823] bg-[#0a1210] flex flex-wrap items-center gap-3 sm:gap-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-extrabold text-white">{selected.display}</h1>
            <span className="text-[10px] px-2 py-0.5 rounded bg-[#00e676]/15 text-[#00e676] font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00e676] pulse-dot"/> LIVE
            </span>
          </div>
          <div className="text-[10px] text-gray-500 mono">
            {selected.provider === 'binance' ? 'Binance Spot · Live WS' : `NEXUS FX PRO ${selected.class.toUpperCase()} · Real-time Feed`}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase">Last</div>
          <div className={`text-2xl font-extrabold mono ${ticker.changePct >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>{fmtPrice(ticker.last, selected.pricePrecision)}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase">24h Change</div>
          <div className={`text-base font-semibold mono flex items-center gap-1 ${ticker.changePct >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>
            {ticker.changePct >= 0 ? <TrendingUp size={13}/> : <TrendingDown size={13}/>}
            {Number.isFinite(ticker.changePct) ? `${ticker.changePct >= 0 ? '+' : ''}${ticker.changePct.toFixed(2)}%` : '-'}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase">24h High</div>
          <div className="text-sm text-white mono" data-testid="ticker-24h-high">{fmtPrice(ticker.high, selected.pricePrecision)}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase">24h Low</div>
          <div className="text-sm text-white mono" data-testid="ticker-24h-low">{fmtPrice(ticker.low, selected.pricePrecision)}</div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase">24h Volume</div>
          <div className="text-sm text-white mono" data-testid="ticker-24h-volume">{
            Number.isFinite(ticker.quoteVolume) && ticker.quoteVolume > 0
              ? `$${(ticker.quoteVolume / 1e6).toFixed(2)}M`
              : '-'
          }</div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2 sm:gap-4">
          <div>
            <div className="text-[10px] text-gray-500 uppercase">Balance</div>
            <div className="text-sm text-white mono">${(user?.balance ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 uppercase">Floating P&L</div>
            <div className={`text-lg font-extrabold mono ${totalPnL >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>
              {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-12 gap-3 sm:gap-4">
        {/* Watchlist */}
        <div className="col-span-12 xl:col-span-2 rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-[#141f1b] flex items-center justify-between">
            <h3 className="text-xs font-bold text-white uppercase tracking-wider">Markets</h3>
            <Activity size={12} className="text-[#00e676] pulse-dot"/>
          </div>
          <div className="flex flex-wrap gap-1 px-2 py-2 border-b border-[#141f1b]">
            <button onClick={() => setAssetClass('all')} className={`px-2 py-1 rounded text-[10px] font-semibold ${assetClass === 'all' ? 'bg-[#00e676] text-black' : 'border border-[#1a2823] text-gray-400 hover:text-white'}`}>All</button>
            {ASSET_CLASSES.map(c => (
              <button key={c.key} onClick={() => setAssetClass(c.key)} className={`px-2 py-1 rounded text-[10px] font-semibold ${assetClass === c.key ? 'bg-[#00e676] text-black' : 'border border-[#1a2823] text-gray-400 hover:text-white'}`}>{c.label}</button>
            ))}
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '480px' }}>
            {(assetClass === 'all' ? ALL_SYMBOLS : ALL_SYMBOLS.filter(s => s.class === assetClass)).slice(0, 8).map(s => {
              const p = prices[s.symbol];
              const active = selected.symbol === s.symbol;
              const cls = ASSET_CLASSES.find(c => c.key === s.class);
              return (
                <button key={s.symbol} onClick={() => setSelected(s)} className={`w-full text-left px-3 py-2 border-b border-[#0f1a17] transition ${active ? 'bg-[#00e676]/10 border-l-2 border-l-[#00e676]' : 'hover:bg-[#0f1a17]'}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-white font-semibold flex items-center gap-1.5">
                      <span className="w-1 h-3 rounded-sm" style={{ background: cls?.color || '#00e676' }}/>
                      {s.display}
                    </span>
                    <span className={`text-[10px] mono ${(p?.changePct ?? 0) >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>
                      {p?.changePct >= 0 ? '+' : ''}{p?.changePct?.toFixed(2) ?? '0.00'}%
                    </span>
                  </div>
                  <div className="text-[11px] mono text-gray-400 mt-0.5">{p ? fmtPrice(p.last, s.pricePrecision) : '—'}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart takes full width without side panels */}
        <div className="col-span-12 xl:col-span-10 rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-[#141f1b] flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1">
              {TIMEFRAMES.map(t => (
                <button key={t.value} onClick={() => setInterval(t.value)} className={`px-2.5 py-1 rounded text-[11px] font-semibold transition ${interval === t.value ? 'bg-[#00e676]/15 text-[#00e676] border border-[#00e676]/30' : 'text-gray-400 hover:text-white border border-transparent'}`}>{t.label}</button>
              ))}
            </div>
            <div className="flex gap-1 items-center">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider mr-1"><LineChartIcon size={11} className="inline mr-1"/>Indicators</span>
              {[
                { key: 'ema', label: 'EMA', color: 'text-[#00e676]' },
                { key: 'sma', label: 'SMA', color: 'text-amber-400' },
                { key: 'bb', label: 'BB', color: 'text-sky-400' },
                { key: 'ichimoku', label: 'ICHI', color: 'text-cyan-400' },
                { key: 'rsi', label: 'RSI', color: 'text-purple-400' },
                { key: 'macd', label: 'MACD', color: 'text-[#00e676]' }
              ].map(ind => (
                <button key={ind.key} onClick={() => toggleIndicator(ind.key)} className={`px-2 py-1 rounded text-[10px] font-bold transition border ${indicators[ind.key] ? `bg-[#0a1a13] border-[#00e676]/30 ${ind.color}` : 'border-[#1a2823] text-gray-500 hover:text-gray-300'}`}>
                  {ind.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-gray-500 mono">{selected.display} · {interval.toUpperCase()} · {market.toUpperCase()}</div>
          </div>
          <div className="flex-1" style={{ minHeight: 440 }}>
            <TradingViewChart symbol={selected.symbol} interval={interval} provider={selected.provider} assetClass={selected.class} indicators={indicators}/>
          </div>
        </div>

        {/* OrderBook removed */}
      </div>

      {/* Order panel + Positions */}
      <div className="grid grid-cols-12 gap-3 sm:gap-4">
        <div className="col-span-12 lg:col-span-4 p-5 rounded-xl border border-[#1a2823] bg-[#0a1210] space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white">Place Order</h3>
            <span className="text-[10px] text-gray-500 mono">{selected.display} · {selected.provider === 'synth' ? selected.class : market}</span>
          </div>
          <div className="flex bg-[#050807] rounded-md p-1 border border-[#1a2823]">
            {['Market', 'Limit', 'Stop'].map(t => (
              <button key={t} onClick={() => setOrderMode(t)} className={`flex-1 py-1.5 text-xs font-semibold rounded transition ${orderMode === t ? 'bg-[#00e676] text-black' : 'text-gray-400 hover:text-white'}`}>{t}</button>
            ))}
          </div>
          {orderMode !== 'Market' && (
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">{orderMode} Price</label>
              <input value={limitPrice} onChange={e => setLimitPrice(e.target.value)} placeholder={fmtPrice(ticker.last, selected.pricePrecision)} className="w-full mt-1 bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-white text-sm mono focus:outline-none focus:border-[#00e676]"/>
            </div>
          )}
          <div>
            <label className="text-[10px] text-gray-400 uppercase tracking-wider">Amount (base)</label>
            <input value={volume} onChange={e => setVolume(e.target.value)} className="w-full mt-1 bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-white text-sm mono focus:outline-none focus:border-[#00e676]"/>
            <div className="mt-2 flex items-center justify-between text-[10px] px-0.5">
              <span className="text-gray-500 uppercase tracking-wider">Leverage</span>
              <select value={leverage} onChange={e => setLeverage(parseInt(e.target.value, 10))} data-testid="leverage-select"
                      className="bg-[#050807] border border-[#00e676]/30 rounded px-2 py-1 text-[#00e676] font-semibold mono text-[10px] focus:outline-none focus:border-[#00e676]">
                <option value="100">1:100</option>
                <option value="200">1:200</option>
                <option value="500">1:500</option>
                <option value="1000">1:1000</option>
                <option value="2000">1:2000</option>
                <option value="5000">1:Unlimited</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500"/> Stop Loss
              </label>
              <input value={sl} onChange={e => setSl(e.target.value)} placeholder="0.00" data-testid="sl-input"
                     className="w-full mt-1 bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-white text-sm mono focus:outline-none focus:border-orange-500"/>
              <div className="mt-1 flex gap-1">
                {[1, 2, 5].map(p => (
                  <button key={p} type="button" onClick={() => applySlPct(p)}
                          className="flex-1 py-0.5 text-[10px] rounded border border-[#1a2823] text-gray-400 hover:border-orange-500/50 hover:text-orange-400">-{p}%</button>
                ))}
                <button type="button" onClick={() => setSl('')} title="Clear"
                        className="px-1.5 py-0.5 text-[10px] rounded border border-[#1a2823] text-gray-500 hover:text-white">×</button>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00e676]"/> Take Profit
              </label>
              <input value={tp} onChange={e => setTp(e.target.value)} placeholder="0.00" data-testid="tp-input"
                     className="w-full mt-1 bg-[#050807] border border-[#1a2823] rounded px-3 py-2 text-white text-sm mono focus:outline-none focus:border-[#00e676]"/>
              <div className="mt-1 flex gap-1">
                {[1, 2, 5].map(p => (
                  <button key={p} type="button" onClick={() => applyTpPct(p)}
                          className="flex-1 py-0.5 text-[10px] rounded border border-[#1a2823] text-gray-400 hover:border-[#00e676]/50 hover:text-[#00e676]">+{p}%</button>
                ))}
                <button type="button" onClick={() => setTp('')} title="Clear"
                        className="px-1.5 py-0.5 text-[10px] rounded border border-[#1a2823] text-gray-500 hover:text-white">×</button>
              </div>
            </div>
          </div>
          {!hasFunds && (
            <Link to="/dashboard/deposit" data-testid="deposit-to-trade-cta"
                  className="block text-center py-2 rounded-md text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition">
              Deposit funds to start trading →
            </Link>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={() => placeOrder('Sell')} disabled={!!placingOrder || !hasFunds} data-testid="place-sell-btn"
                    className="py-3 rounded-md font-bold text-sm bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition disabled:opacity-50 disabled:cursor-not-allowed">
              {placingOrder === 'Sell' ? 'PLACING…' : 'SELL'}
            </button>
            <button onClick={() => placeOrder('Buy')} disabled={!!placingOrder || !hasFunds} data-testid="place-buy-btn"
                    className="py-3 rounded-md font-bold text-sm bg-[#00e676]/15 text-[#00e676] border border-[#00e676]/30 hover:bg-[#00e676]/25 transition disabled:opacity-50 disabled:cursor-not-allowed">
              {placingOrder === 'Buy' ? 'PLACING…' : 'BUY'}
            </button>
          </div>
          <div className="pt-2 border-t border-[#141f1b] text-[10px] text-gray-500 flex justify-between mono">
            <span>Value ≈ ${(((parseFloat(volume) || 0) * (ticker.last || 0)) || 0).toFixed(2)}</span>
            <span>Fee 0.02%</span>
          </div>
          {requiredMargin != null && (
            <div className="text-[10px] flex justify-between mono" data-testid="required-margin">
              <span className="text-gray-400">Req. Margin · {GOLD_CONTRACT_SIZE}oz contract</span>
              <span className="text-white">${requiredMargin.toFixed(2)} <span className="text-gray-600">@ {levLabel}</span></span>
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-8 rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#141f1b] flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-bold text-white">Open Positions ({positionsWithPnL.length})</h3>
            <div className="flex items-center gap-2">
              {marginLevel != null && (
                <span data-testid="margin-level" title="Equity ÷ Used Margin"
                      className={`px-2 py-0.5 rounded text-[10px] font-bold mono border ${marginLevel <= STOP_OUT_LEVEL ? 'border-red-500/50 text-red-400 bg-red-500/10' : marginLevel <= 150 ? 'border-amber-500/50 text-amber-400 bg-amber-500/10' : 'border-[#1a2823] text-gray-400'}`}>
                  Margin {marginLevel.toFixed(0)}%
                </span>
              )}
              <button
                onClick={async () => {
                  const res = await requestNotificationsPermission();
                  setNotifState(res);
                  if (res === 'granted') toast({ title: 'Alerts enabled', description: 'You\u2019ll be pinged on SL / TP hits.' });
                  else if (res === 'denied') toast({ title: 'Alerts blocked', description: 'Enable notifications in your browser settings.' });
                }}
                data-testid="toggle-notifications-btn"
                title={notifState === 'granted' ? 'SL/TP notifications ON' : notifState === 'denied' ? 'Notifications blocked' : 'Enable browser notifications'}
                className={`text-[11px] px-3 py-1 rounded border inline-flex items-center gap-1 ${
                  notifState === 'granted'
                    ? 'border-[#00e676]/40 text-[#00e676] bg-[#00e676]/10'
                    : notifState === 'denied'
                    ? 'border-red-500/40 text-red-400'
                    : 'border-[#1a2823] text-gray-400 hover:border-[#00e676]/40 hover:text-[#00e676]'
                }`}
              >
                {notifState === 'granted' ? <><Bell size={11}/> Alerts ON</>
                  : notifState === 'denied' ? <><BellOff size={11}/> Blocked</>
                  : <><BellOff size={11}/> Enable Alerts</>}
              </button>
              {positions.length > 0 && (
                <button onClick={closeAll} disabled={loadingPos} className="text-[11px] px-3 py-1 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                  {loadingPos ? 'Closing…' : 'Close All'}
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-2 font-medium">Symbol</th>
                  <th className="px-2 font-medium">Market</th>
                  <th className="px-2 font-medium">Side</th>
                  <th className="px-2 font-medium">Volume</th>
                  <th className="px-2 font-medium">Lev</th>
                  <th className="px-2 font-medium">Entry</th>
                  <th className="px-2 font-medium">Mark</th>
                  <th className="px-2 font-medium">SL / TP</th>
                  <th className="px-2 font-medium">Trail</th>
                  <th className="px-2 font-medium text-right">P&L</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {positionsWithPnL.map(p => (
                  <tr key={p.id} className="border-t border-[#141f1b] hover:bg-[#0f1a17]">
                    <td className="px-4 py-3 text-white font-semibold">{p.display || p.symbol}</td>
                    <td className="px-2"><span className="text-[10px] uppercase tracking-wider text-gray-400">{p.market}</span></td>
                    <td className="px-2"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${p.side === 'Buy' ? 'bg-[#00e676]/15 text-[#00e676]' : 'bg-red-500/15 text-red-400'}`}>{p.side.toUpperCase()}</span></td>
                    <td className="px-2 mono text-gray-300">{p.volume}</td>
                    <td className="px-2"><span className="text-[10px] font-bold text-[#00e676]" data-testid={`pos-leverage-${p.id}`}>{(p.leverage || user?.leverage || 1) >= 5000 ? '1:Unlimited' : `1:${p.leverage || user?.leverage || 1}`}</span></td>
                    <td className="px-2 mono text-gray-300">{fmtPrice(p.open_price, p.precision || 2)}</td>
                    <td className="px-2 mono text-white">{fmtPrice(p.currentPrice, p.precision || 2)}</td>
                    <td className="px-2 mono text-gray-500 text-xs">{p.sl ? fmtPrice(p.sl, p.precision || 2) : '-'} / {p.tp ? fmtPrice(p.tp, p.precision || 2) : '-'}</td>
                    <td className="px-2">
                      <TrailControl pos={p} onToggle={togglePositionTrailing}/>
                      {(p.trailing_pct > 0 || p.trailing_distance > 0) && (
                        <div className="text-[9px] text-gray-500 mt-0.5 mono" data-testid={`pos-trail-info-${p.id}`}>
                          {p.trailing_mode === 'price' ? `±${p.trailing_distance}` : `${p.trailing_pct}%`}
                          {p.trailing_activation > 0 ? ` @${p.trailing_activation}${p.trailing_mode === 'price' ? '' : '%'}` : ''}
                        </div>
                      )}
                    </td>
                    <td className={`px-2 mono font-semibold text-right ${p.pnl >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>
                      {p.pnl >= 0 ? '+' : ''}${p.pnl.toFixed(2)}
                      <div className="text-[10px] font-normal opacity-70">{p.pnlPct >= 0 ? '+' : ''}{p.pnlPct.toFixed(2)}%</div>
                    </td>
                    <td className="px-2 text-right">
                      <button onClick={() => closePosition(p.id)} disabled={!!p._closing} data-testid={`close-position-${p.id}`}
                              className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-red-400 hover:border-red-500/40 transition disabled:opacity-50">
                        <XIcon size={12}/>
                      </button>
                    </td>
                  </tr>
                ))}
                {positionsWithPnL.length === 0 && (
                  <tr><td colSpan="11" className="py-10 text-center text-sm text-gray-500">No open positions. Place your first order above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
